import { Localized } from './localizedStrings';
import { SystemRoles, hasAllAppRoles, hasAnyAppRole, systemConfigurationAdminRoles, systemDiagnosticsRoles, systemTenantRoles, systemSystemAdminRoles, systemMemberRoles, systemAnalyticsRoles } from './rolesAndPermissions';

const HOMEKEY: string = 'home';

/**
 * Returns a dashboard navigation link object
 * @returns a navigation link object
 */
function getDashboardLink(): NavLink {
    return {
        name: Localized.get('dashboard'),
        path: '/',
        key: 'home',
        icon: 'SpeedHigh'
    };
}

/**
 * Returns a tenant navigation link object
 * @returns a navigation link object
 */
function getTenantLink(): NavLink {
    return {
        name: Localized.get('tenants'),
        path: '/tenants',
        key: 'tenants',
        icon: 'EMI',
        //isExpanded: true,
        //links: [
        //    {
        //        name: 'Activity',
        //        url: 'http://msn.com',
        //        key: 'key1',
        //        target: '_blank',
        //    },
        //    {
        //        name: 'MSN',
        //        url: 'http://msn.com',
        //        disabled: true,
        //        key: 'key2',
        //        target: '_blank',
        //    },
        //]
    };
}

/**
 * Returns a configuration navigation link object
 * @returns a navigation link object
 */
function getConfigurationLink(): NavLink {
    return {
        name: Localized.get('configuration'),
        path: '/configuration',
        key: 'configuration',
        icon: 'Settings'
    };
};

/**
 * Returns a diagnostics navigation link object
 * @returns a navigation link object
 */
function getDiagnosticsLink(): NavLink {
    return {
        name: Localized.get('diagnostics'),
        path: '/diagnostics',
        key: 'diagnostics',
        icon: 'Diagnostic'
    };
}

/**
 * Returns a "System" navigation link with sub-navigation. Houses operator-facing
 * cross-service surfaces — currently just Reaper's dead-message manager; future
 * system-scoped operations (event log inspector, broker reset, etc.) will land here.
 * @returns a navigation link object
 */
function getSystemLink(): NavLink {
    return {
        name: Localized.get('system'),
        path: '/system',
        key: 'system',
        icon: 'Settings',
        isExpanded: false,
        links: [
            { name: Localized.get('dead-messages'), path: '/system/dead-messages', key: 'system-dead-messages' },
        ],
    };
}

/**
 * Returns a users navigation link object
 * @returns a navigation link object
 */
function getUsersLink(): NavLink {
    return {
        name: Localized.get('users'),
        path: '/users',
        key: 'users',
        icon: 'Group'
    }
}

/**
 * Returns a users navigation link object
 * @returns a navigation link object
 */
function getAuditsLink(): NavLink {
    return {
        name: Localized.get('audits'),
        path: '/audits',
        key: 'audits',
        icon: 'ComplianceAudit'
    }
}

/**
 * Returns an analytics navigation link object
 * @returns a navigation link object
 */
function getAnalyticsLink(): NavLink {
    return {
        name: Localized.get('analytics'),
        path: '/analytics',
        key: 'analytics',
        icon: 'BarChartVertical'
    }
}

/**
 * Returns an apps navigation link object with sub-navigation
 * @returns a navigation link object
 */
function getAppsLink(): NavLink {
    return {
        name: 'Apps',
        path: '/apps',
        key: 'apps',
        icon: 'AppIconDefault',
        isExpanded: false,
        links: [
            {
                name: 'Publishers',
                path: '/apps/publishers',
                key: 'apps-publishers',
            },
            {
                name: 'Catalog',
                path: '/apps/catalog',
                key: 'apps-catalog',
            },
        ],
    }
}

/**
 * Returns a licensing navigation link object with sub-navigation
 * @returns a navigation link object
 */
function getLicensingLink(): NavLink {
    return {
        name: Localized.get('licensing'),
        path: '/licensing',
        key: 'licensing',
        icon: 'CRMServices',
        isExpanded: false,
        links: [
            { name: 'Dashboard', path: '/licensing/dashboard', key: 'licensing-dashboard' },
            { name: 'Billing', path: '/licensing/billing', key: 'licensing-billing' },
            { name: 'Plans', path: '/licensing/plans', key: 'licensing-plans' },
            { name: 'Entitlements', path: '/licensing/entitlements', key: 'licensing-entitlements' },
            { name: 'Meter Types', path: '/licensing/meter-types', key: 'licensing-meter-types' },
            { name: 'Meters', path: '/licensing/meters', key: 'licensing-meters' },
            { name: 'Credit Bundles', path: '/licensing/credit-bundles', key: 'licensing-credit-bundles' },
            { name: 'FOIA Packages', path: '/licensing/foia-packages', key: 'licensing-foia-packages' },
            { name: 'Analytics', path: '/licensing/analytics', key: 'licensing-analytics' },
            { name: 'Usage', path: '/licensing/usage', key: 'licensing-usage' },
        ],
    }
}

/**
 * Returns the primary navigation groups for the user based on their application roles
 * 
 * @param userAppRoles - the user's permitted application roles
 * @returns an array of navigation links that the user is permitted to see
 */
export function getPrimaryNavGroups(userAppRoles: string[]): NavGroup[] {
    const links: NavLink[] = [
        getDashboardLink()
    ];

    if (Array.isArray(userAppRoles)) {
        if (hasAnyAppRole(systemTenantRoles, userAppRoles)) {
            links.push(getTenantLink());
        }

        //if (hasAnyAppRole(systemDiagnosticsRoles, userAppRoles)) {
        //    links.push(getDiagnosticsLink());
        //}

        //if (hasAllAppRoles(systemConfigurationAdminRoles, userAppRoles)) {
        //    links.push(getConfigurationLink());
        //}

        if (hasAnyAppRole(systemMemberRoles, userAppRoles)) {
            links.push(getUsersLink());
        }

        // Add Apps blade for System Admins
        if (hasAnyAppRole(systemSystemAdminRoles, userAppRoles)) {
            links.push(getAppsLink());
        }

        // Add Licensing above Audits
        if (hasAnyAppRole(systemTenantRoles, userAppRoles)) {
            links.push(getLicensingLink());
        }

        // Add Audits at the bottom of the navigation
        if (hasAnyAppRole(systemDiagnosticsRoles, userAppRoles)) {
            links.push(getAuditsLink());
        }

        // Add Analytics blade for users with analytics roles
        if (hasAnyAppRole(systemAnalyticsRoles, userAppRoles)) {
            links.push(getAnalyticsLink());
        }

        // Add the System parent below Analytics — bottom of the nav. Currently houses
        // Dead Messages (Reaper); future operator-scoped surfaces hang off here too.
        if (hasAnyAppRole(systemDiagnosticsRoles, userAppRoles)) {
            links.push(getSystemLink());
        }
    }

    return [
        { links }
    ];
}

/**
 * Returns the navigation key for the specified path
 *
 * @param path - the specified path
 * @returns the navigation key for the specified path
 */
export function getNavKeyFromPath(path: string): string {
    const paths: string[] = path.split('/');
    paths.shift();

    if (paths.length > 0) {
        const navKey: string = paths.join('-');
        if (navKey !== '') {
            return navKey;
        }
    }

    return HOMEKEY;
}